/**
 * @fileoverview Aba Pessoas de Equipe e permissões: a equipe em páginas de 30,
 * com o perfil de acesso, as personalizações ("2 concedidas, 1 negada"), a
 * licença, as filas e o último acesso; busca por nome ou e-mail e filtros por
 * perfil, licença e "com personalizações", feitos pelo servidor. Clicar abre o
 * painel da pessoa. A busca, os filtros e a página ficam com quem chama
 * (voltar do painel da pessoa mantém onde estava).
 */

import React, { useEffect } from "react";
import { InputAdornment, MenuItem, TextField } from "@material-ui/core";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";

import api from "../../services/api";
import { useDebounce } from "../../hooks/useDebounce";
import { i18n } from "../../translate/i18n";
import ModernSelect from "../ModernSelect";
import { LoadError, LoadingState } from "../Settings/LoadState";
import { customizationsLabel, formatDate, formatDateTime, licenseLabel, roleLabel } from "./labels";
import PersonAvatar from "./PersonAvatar";
import useTeamAccessStyles from "./styles";
import useRequest from "./useRequest";

const t = (key, options) => i18n.t(`accessControl.${key}`, options);

const LICENSES = ["full", "attendance", "internal"];
const MAX_QUEUES_SHOWN = 2;
const SEARCH_DELAY_MS = 300;

/** A busca, os filtros e a página da aba, no estado de quem chama. */
export const INITIAL_PEOPLE_QUERY = { search: "", role: "all", license: "all", customized: false, page: 1 };

const queuesLabel = (queues) => {
  if (!queues.length) return t("people.noQueues");
  const names = queues.slice(0, MAX_QUEUES_SHOWN).map((queue) => queue.name);
  const more = queues.length - names.length;
  return more > 0 ? `${names.join(", ")} +${more}` : names.join(", ");
};

const PeopleTab = ({ roles, query, onQueryChange, onOpen }) => {
  const classes = useTeamAccessStyles();
  const search = useDebounce(query.search, SEARCH_DELAY_MS).trim();
  const { role, license, customized, page } = query;
  const members = useRequest(
    () =>
      api
        .get("/access-control/members", {
          params: {
            search: search || undefined,
            role: role === "all" ? undefined : role,
            license: license === "all" ? undefined : license,
            customized: customized ? "true" : undefined,
            page,
          },
        })
        .then(({ data }) => data),
    [search, role, license, customized, page]
  );
  // Busca e filtro novos voltam à primeira página.
  const change = (patch) => onQueryChange({ ...query, ...patch, page: 1 });
  const goTo = (next) => onQueryChange({ ...query, page: next });

  const data = members.data;
  const rows = data?.members || [];
  const total = data?.total || 0;
  const pageSize = data?.pageSize || 1;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const from = total ? (data.page - 1) * pageSize + 1 : 0;
  const to = Math.min(total, (data?.page || 1) * pageSize);

  // A página sumiu (a equipe diminuiu): vai para a última que existe.
  useEffect(() => {
    if (data && total > 0 && data.page > lastPage) onQueryChange({ ...query, page: lastPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, total, lastPage]);

  const column = (key) => <span className={classes.cellLabel}>{t(`people.columns.${key}`)}:</span>;

  return (
    <div className={classes.root}>
      <div className={classes.toolbar}>
        <TextField
          size="small"
          variant="outlined"
          className={classes.search}
          value={query.search}
          onChange={(event) => change({ search: event.target.value })}
          placeholder={t("people.search")}
          inputProps={{ "aria-label": t("people.search") }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} strokeWidth={1.8} aria-hidden="true" />
              </InputAdornment>
            ),
          }}
        />
        <ModernSelect
          size="small"
          value={role}
          onChange={(event) => change({ role: event.target.value })}
          className={classes.filterSelect}
          SelectDisplayProps={{ "aria-label": t("people.columns.role") }}
          MenuProps={{ disableScrollLock: true }}
        >
          <MenuItem value="all">{t("people.allRoles")}</MenuItem>
          {roles.map((value) => (
            <MenuItem key={value} value={value}>
              {roleLabel(value)}
            </MenuItem>
          ))}
        </ModernSelect>
        <ModernSelect
          size="small"
          value={license}
          onChange={(event) => change({ license: event.target.value })}
          className={classes.filterSelect}
          SelectDisplayProps={{ "aria-label": t("people.columns.license") }}
          MenuProps={{ disableScrollLock: true }}
        >
          <MenuItem value="all">{t("people.allLicenses")}</MenuItem>
          {LICENSES.map((value) => (
            <MenuItem key={value} value={value}>
              {licenseLabel(value)}
            </MenuItem>
          ))}
        </ModernSelect>
        <button
          type="button"
          aria-pressed={customized}
          className={[classes.toggleChip, customized ? classes.toggleChipOn : ""].filter(Boolean).join(" ")}
          onClick={() => change({ customized: !customized })}
        >
          {t("people.customized")}
        </button>
      </div>

      <div className={classes.card}>
        <div className={classes.peopleHead} aria-hidden="true">
          <span>{t("people.columns.person")}</span>
          <span>{t("people.columns.role")}</span>
          <span>{t("people.columns.customizations")}</span>
          <span>{t("people.columns.license")}</span>
          <span>{t("people.columns.queues")}</span>
          <span>{t("people.columns.lastAccess")}</span>
        </div>
        {members.status === "loading" && <LoadingState />}
        {members.status === "error" && <LoadError onRetry={members.reload} />}
        {members.status === "ready" && rows.length === 0 && <div className={classes.empty}>{t("people.empty")}</div>}
        <div className={members.pending && data ? classes.refreshing : undefined} aria-busy={members.pending}>
          {rows.map((member) => (
            <button
              key={member.id}
              type="button"
              className={classes.personRow}
              onClick={() => onOpen(member.id)}
              aria-label={t("people.open", { name: member.name })}
            >
              <span className={classes.personCell}>
                <PersonAvatar name={member.name} image={member.profileImage} />
                <span className={classes.nameWrap}>
                  <span className={classes.name} title={member.name}>
                    {member.name}
                  </span>
                  <span className={classes.email} title={member.email}>
                    {member.email}
                  </span>
                </span>
              </span>
              <span>
                <span className={classes.rolePill}>{roleLabel(member.role)}</span>
              </span>
              <span className={classes.cellText}>
                {column("customizations")}
                {member.granted + member.denied > 0 ? (
                  <>
                    {member.granted > 0 && (
                      <span className={classes.granted}>{customizationsLabel({ granted: member.granted })}</span>
                    )}
                    {member.granted > 0 && member.denied > 0 && ", "}
                    {member.denied > 0 && (
                      <span className={classes.denied}>{customizationsLabel({ denied: member.denied })}</span>
                    )}
                  </>
                ) : (
                  "—"
                )}
              </span>
              <span className={classes.cellText}>
                {column("license")}
                {licenseLabel(member.license)}
              </span>
              <span className={classes.cellText}>
                {column("queues")}
                {queuesLabel(member.queues || [])}
              </span>
              <span className={classes.cellText} title={member.lastLoginAt ? formatDateTime(member.lastLoginAt) : undefined}>
                {column("lastAccess")}
                {member.lastLoginAt ? formatDate(member.lastLoginAt) : t("people.never")}
              </span>
            </button>
          ))}
        </div>
        {total > 0 && (
          <div className={classes.pager}>
            <span role="status">{t("people.pageRange", { from, to, total })}</span>
            {lastPage > 1 && (
              <span className={classes.pagerButtons}>
                <button
                  type="button"
                  className={classes.iconButton}
                  onClick={() => goTo(page - 1)}
                  disabled={page <= 1}
                  aria-label={t("people.previousPage")}
                  title={t("people.previousPage")}
                >
                  <ChevronLeft size={16} strokeWidth={1.8} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className={classes.iconButton}
                  onClick={() => goTo(page + 1)}
                  disabled={page >= lastPage}
                  aria-label={t("people.nextPage")}
                  title={t("people.nextPage")}
                >
                  <ChevronRight size={16} strokeWidth={1.8} aria-hidden="true" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PeopleTab;
